import { Button } from '@savvy-kit/components/button';
import { isNil } from 'es-toolkit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { TaxStatusBar } from 'src/components/dashboard/taxes/tax-status-bar';
import { formatDollarAmount } from 'src/utils/formatting';

import type { TaxTranscriptRequestStatus } from '@savvy/constants/generated/gql-enums';

import type { TaxEnrollment, TaxTranscriptRequest } from './tax-utils';
import { castToTaxTranscriptStatus, formatTaxEnrollment, getCurrentTaxFlow } from './tax-utils';

type ClientTaxDetailsProps = {
  readonly name: string;
  readonly taxEnrollment: TaxEnrollment;
  readonly taxTranscriptStatus: TaxTranscriptRequestStatus | null;
  readonly taxTranscriptRequests: readonly TaxTranscriptRequest[] | null | undefined;
  readonly onUpdate?: () => void;
  readonly taxAddendumPending: boolean;
  readonly taxAddendumRequired: boolean;
};

export function ClientTaxDetails({
  name,
  taxEnrollment,
  taxTranscriptStatus,
  taxTranscriptRequests,
  onUpdate,
  taxAddendumPending,
  taxAddendumRequired,
}: ClientTaxDetailsProps) {
  const { enableTaxFiling } = useFlags();
  const enrollmentType = taxEnrollment.enrollmentType;
  const price = taxEnrollment.price;
  const federalReturnStatus = taxEnrollment.federalReturnStatus;
  const stateReturnStatus = taxEnrollment.stateReturnStatus;
  const updatedAt = taxEnrollment.updatedAt;

  const taxTranscriptStatusEnum = taxTranscriptStatus ? castToTaxTranscriptStatus(taxTranscriptStatus) : null;
  const flowType = getCurrentTaxFlow(enrollmentType, !!enableTaxFiling);

  const latestUpdatedAt = getLatestUpdatedAt(taxTranscriptRequests, updatedAt);

  if (enableTaxFiling && flowType === 'filing') {
    return (
      <div className="flex-1 rounded-[8px] bg-slate-50 p-6 max-w-1/2 min-w-[450px]">
        <div className="flex items-start justify-between mb-4">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-lg font-semibold">{name}</h3>
            <p className="text-sm text-secondary">
              {formatTaxEnrollment(enrollmentType)}
              {!isNil(price) && `, ${formatDollarAmount(price, true)}`}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <TaxStatusBar
              hideTitle={true}
              flowType="filing"
              taxTranscriptStatus={taxTranscriptStatusEnum}
              federalReturnStatus={federalReturnStatus}
              stateReturnStatus={stateReturnStatus}
              taxAddendumPending={taxAddendumPending}
              taxAddendumRequired={taxAddendumRequired}
            />
            {!isNil(latestUpdatedAt) && (
              <p className="text-sm text-secondary mb-4">Last updated {latestUpdatedAt.toFormat('MMM d, yyyy')}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 rounded-[8px] bg-slate-50 p-6 max-w-1/2 min-w-[450px]">
      <div className="mb-4 flex items-start justify-between">
        <h3 className="font-semibold">{name}</h3>
        {!enableTaxFiling && onUpdate && (
          <Button variant="secondary" size="sm" onClick={onUpdate}>
            Update
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-secondary">Filing type</p>
          <p className="text-sm">{formatTaxEnrollment(enrollmentType)}</p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-secondary">Billing amount</p>
          <p className="text-sm">{!isNil(price) ? formatDollarAmount(price, true) : 'Not applicable'}</p>
        </div>
      </div>
    </div>
  );
}

function getLatestUpdatedAt(
  transcriptRequests: readonly TaxTranscriptRequest[] | null | undefined,
  enrollmentUpdatedAt: string | null | undefined,
): DateTime | null {
  const transcriptUpdatedAt = !isNil(transcriptRequests?.[0]?.updatedAt)
    ? DateTime.fromISO(transcriptRequests[0].updatedAt, { zone: 'utc' })
    : null;

  const enrollmentDate = !isNil(enrollmentUpdatedAt) ? DateTime.fromISO(enrollmentUpdatedAt, { zone: 'utc' }) : null;

  const allDates = [transcriptUpdatedAt, enrollmentDate].filter((date): date is DateTime => !isNil(date));

  if (allDates.length === 0) {
    return null;
  }

  return allDates.reduce((latest, current) => (current > latest ? current : latest));
}

// TODO: add banner for filing request status
// type GetBannerParams = {
//   readonly flowType: 'transcript' | 'not_interested';
//   readonly isPrimaryClient: boolean;
//   readonly taxTranscriptStatusEnum: TaxTranscriptRequestStatus | null;
// };

// function getBanner({ flowType, isPrimaryClient, taxTranscriptStatusEnum }: GetBannerParams) {
//   if (flowType === 'not_interested') {
//     return null;
//   }

//   const transcriptRequestAvailable = isPrimaryClient;

//   if (!transcriptRequestAvailable) {
//     return (
//       <Banner
//         icon={<MailWarning />}
//         description="We can provide transcripts for the primary client. Co-clients will be available at a later date."
//       />
//     );
//   }

//   if (taxTranscriptStatusEnum === null) {
//     return (
//       <Banner
//         icon={<CircleCheck />}
//         description="Transcript consent form is available. Please reach out to your client to complete the form on the client portal."
//       />
//       //TODO: Update banner after fleshing out email behavior with Hubspot / talk to Ashley
//       //   description="Consent form sent and waiting for signature. Resend the email if needed."
//       //   actionButton={
//       //     <Button
//       //       variant="secondary"
//       //       onClick={() => {}}
//       //     >
//       //       Resend
//       //     </Button>
//       //   }
//       // />
//     );
//   }

//   return null;
// }
